(ns ycweb.components.editor.base
  (:require
   [rum.core :as rum]
   [util.string :as str]
   [util.transform :as trf]
   [ycweb.util.core :as yuc]
   [ycweb.components.common :as cmn]
   [ycweb.components.forms :as forms]
   [ycweb.components.grid :as grid]
   [ycweb.components.datepicker :as datepicker]
   [ycweb.util.comms :as comms]
   [ycweb.components.toaster :as toaster]
   [ycweb.components.headings :as headings]
   [ycweb.components.datatables.datatables :as datatables]))

;;TODO -> Elevator pitch on how this namespace works

(def editor-cursor-prefix [:datatables :editor])

(defn editor-cursor
  "Get the correct namespaced editor atom inside a global app-atom"
  ([app-atom]
   (rum/cursor-in app-atom editor-cursor-prefix))
  ([app-atom cursor]
   (rum/cursor-in app-atom (conj editor-cursor-prefix cursor))))

(defn editor-overlay [app-atom action]
  (cond
    (= action :open)
    (toaster/open-overlay app-atom :modal/modal-loading)

    (= action :close)
    (toaster/close-overlay app-atom :modal/modal-loading)))

(def button-lookup
  {:create
   {:text "Create"
    :action :editor/create
    :config {:mode :create}}
   :edit
   {:text "Update"
    :action :editor/update
    :config {:mode :edit}}
   :remove
   {:text "Delete"
    :action :editor/delete
    :config {:mode :remove}}
   :cancel
   {:text "Cancel"
    :action :close-modal
    :config {}}})

(defn reset-error [app-atom {:keys [editor-name] :as editor-cfg}]
  (let [editor-atom (editor-cursor app-atom)]
    (swap! editor-atom update editor-name
      (fn [editor]
        (let [fields (:fields editor)
              cleaned-inputs
              (reduce
                (fn [acc field]
                  (let [field-name (:name field)
                        ;; Circle back to see if vector? needed
                        field-path (if (vector? field-name) field-name [field-name])]
                    (update-in acc field-path dissoc :field-error)))
                (:form editor) fields)]
          (assoc editor
            :error ""
            :form cleaned-inputs))))))

(defn update-config [app-atom {:keys [editor-name] :as editor-cfg} updated-config]
  ;;This is for trigger events or updating top level editor information.
  (let [editor-atom (editor-cursor app-atom)]
    (swap! editor-atom update editor-name
      (fn [editor-config]
        (let [a-config
              (when (:assoc updated-config)
                (merge editor-config (:assoc updated-config)))
              d-config
              (when (:dissoc updated-config)
                (apply (partial dissoc (or a-config editor-config)) (:dissoc updated-config)))]
          (or d-config a-config editor-config))))))

(defn update-form-fields [app-atom editor-name input-configs]
  (let [editor-atom (editor-cursor app-atom)
        fields (cmn/curact editor-atom [editor-name :fields])
        kv-pairs
        (reduce
          (fn [acc input-config]
            (assoc acc (:name input-config) (:column-idx input-config)))
          {} fields)]
    (swap! editor-atom update editor-name
      (fn [editor-cfg]
        (->
          editor-cfg
          (update :form
            (fn [form]
              (reduce
                (fn [acc [field-name field-config]]
                  (cond
                    (get field-config :form)
                    (update acc field-name merge (get field-config :form))

                    :else
                    acc))
                form input-configs)))
          (update :fields
            (fn [fields]
              (reduce
                (fn [acc [field-name field-config]]
                  (let [field-index (get kv-pairs field-name)]
                    (cond
                      (and
                        field-index
                        (get field-config :field))
                      (update acc field-index merge (get field-config :field))

                      :else
                      acc)))
                fields input-configs))))))))

(defn get-field-options [app-atom editor-name field-name]
  (let [editor-atom (editor-cursor app-atom editor-name)
        fields (cmn/curact editor-atom :fields)
        kv-pairs
        (reduce
          (fn [acc input-config]
            (assoc acc (:name input-config) (:column-idx input-config)))
          {} fields)
        column-idx (get kv-pairs field-name)]

    (cond
      (nil? column-idx)
      []

      :else
      (cmn/curact editor-atom
        [:fields column-idx :options]))))

(defn editor-error [app-atom editor-name error]
  (let [editor-atom (editor-cursor app-atom)]
    (swap! editor-atom update editor-name assoc :error error)
    {:success false}))

(defn editor-field-errors [app-atom editor-name field-errors]
  (let [editor-atom (editor-cursor app-atom editor-name)
        {:keys [fields datatable]} @editor-atom
        datatable-atom (datatables/table-cursor app-atom datatable)
        {:keys [row-id]} (:config @datatable-atom)
        hidden-fields (filter #(= (:type %) :hidden) fields)
        hidden-field-names (set (conj (map :name hidden-fields) row-id))
        hidden-errors
        (->>
          field-errors
          (filter #(contains? hidden-field-names (:field-name %))))]

    (cond
      (not (empty? hidden-errors))
      (let [str-list-hidden-errors
            (map
              (fn [hidden-error]
                (let [errors (str/join (:field-status hidden-error ", "))]
                  errors))
              hidden-errors)]
        (editor-error app-atom editor-name str-list-hidden-errors))

      :else
      (swap! editor-atom update :form
        (fn [form]
          (reduce
            (fn [acc {:keys [field-name field-status]}]
              ;; TODO Circle back to need for vector?
              (let [field-path (if (vector? field-name) field-name [field-name])
                    field (get-in acc field-path)]
                (if field
                  (assoc-in acc (conj field-path :field-error) field-status)
                  acc)))
            form field-errors))))
    {:success false}))

(rum/defc build-input-select < rum/reactive
  [app-atom
   {:keys [editor-name editor-fns] :as editor-cfg}
   {:keys [label attr opts-cfg idx] :as field}]
  (let [editor-atom (editor-cursor app-atom editor-name)
        input-atom
        (rum/cursor-in editor-atom [:form (:name field)])
        filter-fn (get-in editor-fns [:list-fns (:filter-fn attr)])
        field-fns (get-in editor-fns [:form :fields (:name field)])
        change-fn (:change-fn field-fns)
        options (if filter-fn (filter-fn app-atom (:options field)) (:options field))
        value-key (or (get-in opts-cfg [:lookup :value]) :value)
        text-key (or (get-in opts-cfg [:lookup :text]) :text)]

    (forms/select label (rum/cursor input-atom :value)
      (concat
        (cond
          (and
            (yuc/presence (:placeholder attr))
            (string? (yuc/presence (:placeholder attr))))
          [{:text (:placeholder attr)
            :value (:placeholder attr)}]

          (and
            (yuc/presence (:placeholder attr))
            (map? (yuc/presence (:placeholder attr))))
          (let [text (get-in attr [:placeholder text-key])
                value (get-in attr [:placeholder value-key])]
            [{:text text
              :value value}])

          :else
          [])

        (map
          (fn [t]
            {:value (str (or (get t value-key) (get t text-key)))
             :text (get t text-key)})
          (or options [])))
      (yuc/maybe assoc
        {:small? (if (boolean? (:small? attr)) (:small? attr) true)}
        :disabled (:disabled attr)
        :hint (:hint attr)
        :type (:type attr)
        :change-fn
        (when change-fn
          (fn [_ v]
            (change-fn app-atom editor-atom v)))))))

(rum/defc build-input-select-search < rum/reactive
  [app-atom
   {:keys [editor-name editor-fns] :as editor-cfg}
   {:keys [label attr opts-cfg idx] :as field}]
  ;;TODO: Error if multi-select is enabled
  ;; doesn't make sense to have a multi option on a select list
  ;; with multiple rows selected too. Wouldn't know what you're editing..
  (let [editor-atom (editor-cursor app-atom editor-name)
        input-atom (rum/cursor-in editor-atom [:form (:name field)])
        field-atom (rum/cursor-in editor-atom [:fields idx])
        filter-fn (get-in editor-fns [:list-fns (:filter-fn attr)])
        field-fns (get-in editor-fns [:form :fields (:name field)])
        options (if filter-fn (filter-fn app-atom (:options field)) (:options field))
        multi-select? (trf/toboolean (yuc/presence (get opts-cfg :multi-select)))
        value-key (or (get-in opts-cfg [:lookup :value]) :value)
        text-key (or (get-in opts-cfg [:lookup :text]) :text)

        option-value (cmn/curact input-atom :value)
        options-idx (yuc/index-by value-key options)

        items-selected?
        (if multi-select?
          (pos? (count option-value))
          (trf/toboolean (yuc/presence option-value)))

        select-key
        (or
          (:selected-key opts-cfg)
          :selected?)

        custom-display-fn (:display-fn field-fns)
        display-item
        (if (yuc/presence custom-display-fn)
          ;; A multi-select's value is the selection itself, not a key to look up.
          (custom-display-fn app-atom
            (if multi-select? option-value (get-in options-idx [option-value])))
          (get-in options-idx [option-value text-key]))

        custom-select-fn (:select-fn field-fns)
        select-fn
        (if (yuc/presence custom-select-fn)
          #(custom-select-fn app-atom input-atom
             {:item-selector value-key :selected-key select-key :editor-name editor-name} %)
          #(swap! input-atom assoc :value (get % value-key)))

        custom-item-render-fn (:item-render-fn field-fns)
        item-render-fn
        (if (yuc/presence custom-item-render-fn)
          #(custom-item-render-fn app-atom input-atom
             {:item-selector value-key :selected-key select-key} %)
          #(get % text-key))

        custom-search-fn (:search-fn field-fns)
        search-fn
        (or
          custom-search-fn
          #(str/includes?
             (str/lower-case (str (get %1 text-key)))
             (str/lower-case %2)))]

    [:div
     {:class (yuc/bem :form-field [])}
     (forms/field-label label {})
     (forms/inline-autocomplete-select
       (if items-selected?
         [:div
          {:style
           {:padding-top "10px"
            :overflow "hidden"
            :text-overflow "ellipsis"
            :white-space "nowrap"}}
          display-item]
         [:span.u-block.u-margin-top-12.faux-input-combo__placeholder (:placeholder attr)])
       options
       {:select-style? true
        :force-open? true
        :min-count 2
        :small? (if (boolean? (:small? attr)) (:small? attr) true)
        :initial-list options
        :select-fn select-fn
        :item-render-fn item-render-fn
        ;; A multi-select stays open so several can be ticked in one go.
        :close-on-select? (not multi-select?)
        :filter-fn search-fn})
     (when (yuc/presence (:hint attr))
       [:span.text-input--tiny (:hint attr)])]))

(rum/defc build-input-date < rum/reactive
  [app-atom
   {:keys [editor-name] :as editor-cfg}
   {:keys [label attr opts] :as field}]
  (let [full-cursor-path
        (concat
          editor-cursor-prefix
          [editor-name :form (:name field) :value])]

    (datepicker/datepicker app-atom label "dd-mm-yyyy"
      full-cursor-path
      {:datatype :date/dmy
       :requires-action true
       :style {:max-width "380px"}})))

(rum/defc build-input-checkbox < rum/reactive
  [app-atom
   {:keys [editor-name editor-fns] :as editor-cfg}
   {:keys [label attr opts-cfg] :as field}]
  (let [editor-atom (editor-cursor app-atom editor-name)
        input-atom (rum/cursor-in editor-atom [:form (:name field)])]

    (forms/checkbox label (rum/cursor input-atom :value) attr)))

(rum/defc build-input-radio < rum/reactive
  [app-atom
   {:keys [editor-name editor-fns] :as editor-cfg}
   {:keys [label attr opts-cfg] :as field}]
  (let [editor-atom (editor-cursor app-atom editor-name)
        input-atom (rum/cursor-in editor-atom [:form (:name field)])
        options (:options field)
        value-key (or (get-in opts-cfg [:lookup :value]) :value)
        text-key (or (get-in opts-cfg [:lookup :text]) :text)

        field-fns (get-in editor-fns [:form :fields (:name field)])
        change-fn (:change-fn field-fns)
        radio-options
        (map
          (fn [option]
            {:label (str (or (get option text-key) option))
             :value (str (or (get option value-key) (get option text-key) option))
             :options
             (yuc/maybe assoc
               opts-cfg
               :change-fn
               (when change-fn
                 (fn [_ v]
                   (change-fn app-atom editor-atom v))))})
          options)]

    [:div
     (forms/field-label label {})
     (forms/radios (rum/cursor input-atom :value)
       radio-options attr)]))

(rum/defc build-input-hidden < rum/reactive
  [app-atom
   {:keys [editor-name] :as editor-cfg}
   {:keys [attr] :as field}]
  (let [editor-atom (editor-cursor app-atom editor-name)
        input-atom
        (rum/cursor-in editor-atom
          [:form (:name field)])]
    (forms/input "" "" (rum/cursor input-atom :value)
      (yuc/maybe
        assoc
        {:type :hidden
         :label-hidden? true
         :required? (or (:required attr) false)}
        :default (:default attr)))))

(rum/defc build-input-default < rum/reactive
  [app-atom
   {:keys [editor-name editor-fns] :as editor-cfg}
   {:keys [label attr idx] :as field}]
  (let [editor-atom (editor-cursor app-atom editor-name)
        {:keys [field-idx-lookup fields]} (rum/react editor-atom)
        field-idx (get field-idx-lookup (:name field))
        field-config (get fields field-idx)
        input-atom (rum/cursor-in editor-atom [:form (:name field)])
        field-fns (get-in editor-fns [:form :fields (:name field)])
        change-fn (:change-fn field-fns)]

    (forms/input label (:placeholder attr) (rum/cursor input-atom :value)
      (yuc/maybe
        assoc
        {:type (or (:type attr) :text)
         :required? (or (:required attr) false)
         :label-hidden? (or (:label-hidden? attr) false)}
        :default (:default attr)
        :class (:class attr)
        :style (:style attr)
        :hint (or (:hint field-config) (:hint attr))
        :disabled (:disabled attr)
        :auto-focus (:auto-focus attr)
        :submit-fn (:submit-fn attr)
        :no-icon true
        :onblur-fn (:onblur-fn attr)
        :change-fn
        (when change-fn
          (fn [_ v]
            (change-fn app-atom editor-atom v)))))))

;;TODO checkbox, radio, datetime, password, readonly, textarea, upload
(rum/defc build-input-field < rum/reactive
  [app-atom editor-cfg {:keys [label attr] :as field}]
  (let [editor-atom (editor-cursor app-atom (:editor-name editor-cfg))
        input-path [:form (:name field)]
        input-atom (rum/cursor-in editor-atom input-path)
        value-atom (rum/cursor input-atom :value)
        field-message (cmn/curact input-atom :field-message)
        field-info (cmn/curact input-atom :field-info)
        field-error (cmn/curact input-atom :field-error)
        multi-values (cmn/curact input-atom :multi-values)
        multi-editable (cmn/curact input-atom :multi-editable)
        override-multi-value (cmn/curact input-atom :override-multi-value)
        field-fns (get-in editor-cfg [:editor-fns :form :fields (:name field)])
        hidden-fn (get field-fns :hidden-fn)
        hidden? (when hidden-fn (hidden-fn app-atom))
        render-fn (get field-fns :render-fn)
        multi-editable?
        (or
          (not (yuc/presence multi-editable))
          (and (yuc/presence multi-editable) (not multi-editable)))]

    [:div.input-wrapper
     {:style
      (yuc/maybe assoc
        {:position "relative"}
        :margin-bottom (when (not= (:type field) :hidden) "35px"))}
     (when-not hidden?
       [:div
        [:div.input
         (cond
           render-fn
           (render-fn app-atom editor-cfg field)

           (= (:type field) :select)
           (build-input-select app-atom editor-cfg field)

           (= (:type field) :select-search)
           (build-input-select-search app-atom editor-cfg field)

           (= (:type field) :date)
           (build-input-date app-atom editor-cfg field)

           (= (:type field) :checkbox)
           (build-input-checkbox app-atom editor-cfg field)

           (= (:type field) :radio)
           (build-input-radio app-atom editor-cfg field)

           (= (:type field) :hidden)
           (build-input-hidden app-atom editor-cfg field)

           :else
           (build-input-default app-atom editor-cfg field))]

        (when (and multi-values (not override-multi-value))
          [:div.multi-value
           {:style
            {:position "absolute"
             :top "0px"
             :border "1px dotted #666"
             :border-radius "3px"
             :background-color "#fafafa"
             :cursor (if multi-editable? "pointer" "default")
             :padding "5px"}
            :on-click
            (when multi-editable?
              #(swap! input-atom update :override-multi-value not))}
           [:span "Multiple values"]
           [:br]
           [:span.multi-info
            {:style
             {:font-size "10px"
              :line-height "1.25em"}}
            (cond
              multi-editable?
              "The selected items contain different values for this input.
              To edit and set all items for this input to the same value, click or tap here,
              otherwise they will retain their individual values."

              :else
              "This input can be edited individually, but not part of a group")]])
        [:div
         {:style
          {:position "absolute"
           :bottom "-45px"
           :width "100%"}}
         (when
           (and multi-values (yuc/presence override-multi-value))
           [:div.multi-restore
            {:style
             {:font-size "10px"
              :text-decoration "underline"
              :cursor "pointer"}
             :on-click
             #(swap! editor-atom update-in input-path
                (fn [input]
                  (->
                    input
                    (dissoc :value :field-error)
                    (assoc :override-multi-value false))))}
            "Undo Changes"])

         (when (and field-error (not= (:type field) :hidden))
           [:div.field-error.error.text-input--tiny
            (first field-error)])

         (when (and field-message (not= (:type field) :hidden))
           [:div.field-message.text-input--tiny field-message])

         (when (and field-info (not= (:type field) :hidden))
           [:div.field-info.text-input--tiny field-info])]])]))

(defn default-value [editor-field]
  (cond
    (contains? #{:text :date} (:type editor-field))
    (get-in editor-field [:attr :default])

    (or
      (= (:type editor-field) :radio)
      (= (:type editor-field) :checkbox))
    (or (get-in editor-field [:attr :default]) false)

    (= (:type editor-field) :select)
    (or
      (get-in editor-field [:attr :default])
      (get-in editor-field [:attr :placeholder])
      #_(get-in editor-field
          [:options 0
           (or
             (get-in editor-field [:opts-cfg :lookup :value])
             :value)]))))

(defn common-value [selected-rows column editor-field]
  (let [values (set (map #(datatables/cell-data % column :edit) selected-rows))]
    (cond
      (= (count values) 1)
      {:value (first values) :multi-values false}

      (> (count values) 1)
      {:value nil :multi-values true}

      :else
      {:value (default-value editor-field) :multi-values false})))

(defn reset-fields [app-atom {:keys [mode editor editor-fns] :as modal-config} selected-rows]
  (let [editor-atom (editor-cursor app-atom)
        {:keys [datatable] :as editor-cfg}
        @(rum/cursor editor-atom editor)

        datatable-atom (datatables/table-cursor app-atom datatable)
        {:keys [column-defs]} (:config @datatable-atom)

        column-defs-idx (yuc/index-by #(or (:edit-field %) (:key %)) column-defs)

        reset-field-list
        (->>
          (:fields editor-cfg)
          (filter
            (fn [v]
              (let [hidden-fn (get editor-fns (:hidden-fn v))]
                (or (nil? hidden-fn) (not (hidden-fn app-atom))))))
          (reduce
            (fn [acc v]
              (let [value
                    (cond
                      (and (= mode :edit) (= (count selected-rows) 1))
                      (let [table-row (first selected-rows)
                            table-column
                            (or
                              (get column-defs-idx (:name v))
                              ;;If it doesn't exist as a defined column in the table's col-defs
                              ;;Then it most likely exists in the underlying data structure
                              ;;So we "fake" the column
                              {:key (:name v)})

                            attribute-type (get-in v [:attr :type] :none)
                            cell-value (datatables/cell-data table-row table-column :edit)
                            coerced-value
                            (case attribute-type
                              :currency (trf/fromcurrency cell-value)
                              :none cell-value
                              cell-value)]

                        {:value coerced-value
                         :multi-values false})

                      (and (= mode :edit) (> (count selected-rows) 1))
                      (common-value selected-rows (get column-defs-idx (:name v)) v)

                      :else
                      {:value (default-value v) :multi-values false})]
                (assoc acc (:name v) value)))
            {}))]
    (swap! editor-atom update editor assoc
      :error ""
      :form reset-field-list)))

(defn edit-field-bubble [editor-type config app-atom col row v]
  (println "edit-field-bubble: not yet implemented"))

(defn edit-field-inline
  [editor-name {:keys [on-open onblur-fn classes style editor-fns]} app-atom col row cell-val]
  (let [editor-atom (editor-cursor app-atom)
        {:keys [datatable fields submit-all? eager-submit?] :as editor-cfg} @(rum/cursor editor-atom editor-name)
        {:keys [row-id column-defs] :as dt-config} (:config @(datatables/table-cursor app-atom datatable))

        column-defs-idx (yuc/index-by #(or (:edit-field %) (:key %)) column-defs)
        ;;TODO: This is an unfortunate work around that will need to be removed
        ;; once the underyling data shape has been refactored for the plan-items table / to use with inline editing
        hidden-fields
        (filter
          #(or
             submit-all?
             (= (:type %) :hidden))
          fields)

        form-fields
        (map
          (fn [hidden-field]
            (let [editor-field-name (:name hidden-field)
                  column-def (get column-defs-idx editor-field-name)]
              (->
                hidden-field
                (assoc
                  editor-field-name
                  {:value
                   (datatables/cell-data row
                     (assoc column-def :edit-field editor-field-name)
                     :edit)})
                (dissoc :name :type :attrs :label))))
          hidden-fields)

        idx-editor-fields (yuc/index-by :name fields)
        editor-field (get idx-editor-fields (:key col))
        btn-config (get button-lookup :edit)
        selected-row-id (get row row-id)

        atom-path [editor-name :form (:name editor-field) :value]

        submit-fn
        (fn []
          (let [current-value @(rum/cursor-in editor-atom atom-path)]
            (when (and (not (nil? current-value)) (or eager-submit? (not= current-value cell-val)))
              (grid/dispatch-action app-atom (:action btn-config)
                (assoc (:config btn-config) :editor-name editor-name)))))

        onblur-fn
        (cond
          (and (keyword? onblur-fn) (= onblur-fn :submit))
          submit-fn

          (fn? onblur-fn)
          onblur-fn)

        input-hint-fn (:input-hint-k editor-fns)]

    (swap! editor-atom update editor-name assoc
      :error ""
      :mode :inline
      :form
      (cond
        (= on-open :clear)
        (apply merge form-fields)

        :else
        (assoc (apply merge form-fields) (:name editor-field)
          {:editing true
           :value cell-val})))

    (build-input-field app-atom editor-cfg
      (update editor-field :attr assoc
        :hint (when input-hint-fn (input-hint-fn app-atom row col cell-val))
        :label-hidden? true
        :auto-focus true
        :class classes
        :style style
        :submit-fn submit-fn
        :onblur-fn
        (fn []
          (datatables/close-inline app-atom datatable selected-row-id)
          (onblur-fn))))))

(defn edit-field [editor-name edit-type config]
  (cond
    (= edit-type :inline)
    (partial edit-field-inline editor-name config)

    (= edit-type :bubble)
    (partial edit-field-bubble editor-name config)))

(rum/defc editor-body-column-field [app-atom columns config field]
  [:div
   {:class (yuc/bem :flex-grid-block [(keyword (str "m-1-" columns))])}
   [:div.flex-grid-content
    (build-input-field app-atom config field)]])

(rum/defc editor-body < rum/reactive [app-atom config-atom]
  (let [{:keys [editor editor-fns mode message title description] :as modal-config} (:config @config-atom)
        editor-atom (editor-cursor app-atom editor)
        config (assoc @editor-atom :editor-fns editor-fns)
        fields (cmn/curact editor-atom :fields)
        {:keys [width columns]} (:style modal-config)]
    [:div
     {:style {:overflow-y "visible"}}
     (headings/heading title
       {:type :h2 :size :default
        :subtext description})
     (cond
       (or (= mode :create) (= mode :edit))
       (into
         [:form
          {:class
           (remove nil?
             [(when (or (nil? width) (= width :small)) :u-max-width-480)
              (when (or (nil? width) (= width :small)) :u-block-center)])}]
         (cond
           (yuc/presence columns)
           ;; Hidden fields are dropped before the grid is laid out, not merely
           ;; emptied — an empty cell still takes its place in the row and pushes
           ;; whatever follows onto the next one.
           (let [shown-fields
                 (remove
                   (fn [field]
                     (when-let [hidden-fn (get-in editor-fns [:form :fields (:name field) :hidden-fn])]
                       (hidden-fn app-atom)))
                   fields)

                 [other-fields grouping-fields]
                 (yuc/separate
                   (fn [field]
                     (or
                       (= (:type field) :hidden)
                       (yuc/presence (:class field))))
                   shown-fields)
                 fields-gpd (partition-all columns grouping-fields)]
             (concat
               (map-indexed
                 (fn [idx fields]
                   (into
                     [:div {:class (yuc/bem :flex-grid-group [:flush-force :align-left])}]
                     (map-indexed
                       (fn [child-idx field]
                         (rum/with-key
                           (editor-body-column-field app-atom (count fields) config field)
                           (str idx "-" child-idx)))
                       fields)))
                 fields-gpd)
               (map-indexed
                 (fn [idx field]
                   (rum/with-key
                     (build-input-field app-atom config field)
                     idx))
                 other-fields)))

           :else
           (map-indexed
             (fn [idx field]
               (rum/with-key
                 (build-input-field app-atom config field)
                 idx))
             fields)))

       (= mode :remove)
       [:div
        (or message "Are you sure you want to delete this row?")]

       :else
       "Error: Invalid mode selected. Please contact one of the developers")]))

(defmethod grid/modal-config :editor/modal [app-atom config tp]
  (let [modal-config (cmn/curact (grid/modal-cursor app-atom) :config)
        {:keys [style] :as cfg} (:config modal-config)]
    {:body editor-body
     :footer grid/modal-editor-footer
     :modal-type [(:width style :small) :variable]}))

(defmethod grid/action :editor/open-modal
  [app-atom btn-config]
  (let [editor-atom (editor-cursor app-atom (:editor btn-config))
        {:keys [datatable end-points style] :as config} @editor-atom

        selected-rows-idx (datatables/rows app-atom datatable (:selected-row-ids btn-config))
        selected-rows (vals selected-rows-idx)

        end-point (get end-points (:extends btn-config))
        editor-fns (:editor-fns btn-config)
        event-fns (:event-fns editor-fns)
        open-event-fn (:open-fn event-fns)
        pre-open-event-fn (:pre-open-fn event-fns)

        buttons
        (if (:buttons btn-config)
          (vec
            (map
              #(if (keyword? %) (get button-lookup (:extends btn-config)) %)
              (:buttons btn-config)))
          [(merge (get button-lookup (:extends btn-config)) (:config btn-config))
           (get button-lookup :cancel)])

        mode
        (when (:extends btn-config)
          (get-in (get button-lookup (:extends btn-config)) [:config :mode]))

        modal-config
        (yuc/maybe assoc
          {:title (or (:title btn-config) (str (str/capitalize (name (:extends btn-config))) " an entry"))
           :message (when (:message btn-config) (:message btn-config))
           :end-point end-point
           :overlay? true
           :mode mode
           :editor (:editor btn-config)
           :editor-fns editor-fns
           :buttons buttons}
          :style style
          :editor-style {:overflow-y "visible"}
          :description (:description btn-config))

        {:keys [prevent-default]}
        (when pre-open-event-fn
          (pre-open-event-fn app-atom editor-atom selected-rows-idx mode))]

    (when (not prevent-default)
      (grid/open-modal app-atom :editor/modal {:config modal-config})
      (reset-fields app-atom modal-config selected-rows)
      (when open-event-fn
        (open-event-fn app-atom editor-atom mode)))))

(defn pre-submit [app-atom editor-cfg]
  (when (grid/modal-open? app-atom)
    (editor-overlay app-atom :open))
  (reset-error app-atom editor-cfg))

(defn form-submission-values [form fields mode]
  (let [resp
        (reduce
          (fn [acc field]
            (let [field-name (:name field)
                  config (get form field-name)]

              (cond
                (and
                  config
                  (or
                    (and
                      (:multi-values config)
                      (not (:override-multi-value config)))
                    (and
                      (yuc/presence (:multi-editable config))
                      (not (:multi-editable config)))))
                acc

                (not= mode :inline)
                (let [field-value
                      (cond
                        (and
                          (= (:type field) :select-search)
                          (yuc/indexed-map? (:value config)))
                        (vals (:value config))

                        (= (:type field) :select)
                        (let [option-value (:value config)
                              placeholder (get-in field [:attr :placeholder])
                              value-key (or (get-in field [:opts-cfg :lookup :value]) :value)
                              placeholder-value
                              (cond
                                (map? placeholder)
                                (get placeholder value-key)

                                :else
                                placeholder)]

                          (when (not= placeholder-value option-value)
                            option-value))

                        (and
                          (= :currency (get-in field [:attr :type]))
                          (= (:type field) :number))
                        (trf/tocurrency (:value config))

                        :else
                        (:value config))]
                  (assoc acc field-name field-value))

                (and
                  (= mode :inline)
                  (:value config))
                (yuc/maybe assoc
                  acc
                  field-name (:value config)
                  :edited-field (when (:editing config) field-name))

                :else
                acc)))
          {} fields)]
    resp))

(defmethod grid/action :editor/create [app-atom btn-config]
  (let [session-id (cmn/curact app-atom :php-session-id)
        editor-atom (editor-cursor app-atom (:editor-name btn-config))
        form-atom (rum/cursor editor-atom :form)
        {:keys [end-points fields mode] :as editor-cfg} @editor-atom
        form-data (form-submission-values @form-atom fields mode)

        editor-fns (:editor-fns (:config btn-config))
        event-fns (:event-fns editor-fns)
        pre-submit-fn (:pre-submit-fn event-fns)
        pre-submit-data (when pre-submit-fn (pre-submit-fn app-atom editor-atom mode))]

    (pre-submit app-atom editor-cfg)
    (comms/send
      (get end-points (:mode btn-config))
      {:action (:mode btn-config)
       :session/php-session-id session-id
       :data [form-data]
       :pre-submit-data pre-submit-data})))

(defmethod grid/action :editor/update [app-atom btn-config]
  (let [session-id (cmn/curact app-atom :php-session-id)
        editor-atom (editor-cursor app-atom (:editor-name btn-config))
        event (rum/cursor editor-atom :event)
        form-atom (rum/cursor editor-atom :form)
        {:keys [datatable end-points fields mode] :as editor-cfg} @editor-atom

        datatable-atom (datatables/table-cursor app-atom datatable)
        {:keys [row-id]} (:config @datatable-atom)

        selected-rows-atom (rum/cursor datatable-atom :select)
        ;;TODO: This is a work around until the selection of rows is reworked to separate out the
        ;; selected/rows being edited vs the highlighting of the row && data shape has been reworked for plan-items
        selected-row-ids (or @selected-rows-atom (get btn-config row-id) #{(get-in @form-atom [row-id :value])})
        rows (datatables/rows app-atom datatable selected-row-ids)
        form-data (form-submission-values @form-atom fields mode)

        editor-fns (:editor-fns (:config btn-config))
        event-fns (:event-fns editor-fns)
        pre-submit-fn (:pre-submit-fn event-fns)
        pre-submit-data (when pre-submit-fn (pre-submit-fn app-atom editor-atom mode))]

    (when-not (= @event :processing)
      (pre-submit app-atom editor-cfg)
      (update-config app-atom editor-cfg {:assoc {:event :processing}})
      (comms/send
        (get end-points (:mode btn-config))
        {:action (:mode btn-config)
         :session/php-session-id session-id
         :pre-submit-data pre-submit-data
         :data
         (reduce
           (fn [acc selected-id]
             (conj acc
               (assoc
                 form-data
                 row-id selected-id
                 :row (get rows selected-id))))
           [] selected-row-ids)}))))

(defmethod grid/action :editor/delete [app-atom btn-config]
  (let [session-id (cmn/curact app-atom :php-session-id)
        editor-atom (editor-cursor app-atom (:editor-name btn-config))
        {:keys [datatable end-points mode] :as editor-cfg} @editor-atom

        datatable-atom (datatables/table-cursor app-atom datatable)
        {:keys [row-id]} (:config @datatable-atom)

        selected-rows-atom (rum/cursor datatable-atom :select)
        selected-row-ids @selected-rows-atom

        editor-fns (:editor-fns (:config btn-config))
        event-fns (:event-fns editor-fns)
        pre-submit-fn (:pre-submit-fn event-fns)
        pre-submit-data (when pre-submit-fn (pre-submit-fn app-atom editor-atom mode))]

    (pre-submit app-atom editor-cfg)
    (comms/send
      (get end-points (:mode btn-config))
      {:action (:mode btn-config)
       :session/php-session-id session-id
       :pre-submit-data pre-submit-data
       :data (reduce #(conj %1 {row-id (str %2)}) [] selected-row-ids)})))

(defn post-comms
  "For now just manages select lists but could do all other form fields"
  [app-atom editor-name {:keys [options]}]
  (let [editor-atom (editor-cursor app-atom)
        current-editor (get @editor-atom editor-name)
        current-editor-fields (:fields current-editor)
        fields-with-options
        (->>
          current-editor-fields
          (map-indexed #(assoc %2 :idx %1))
          (filter #(contains? #{:select :select-search :radio} (:type %))))]

    (when (nil? current-editor)
      (toaster/open-toast app-atom :error (str "Editor config not initiated for editor " editor-name ", post comms must happen after init") {}))
    (swap! editor-atom update editor-name
      (fn [editor]
        (let [updated-fields
              (reduce
                (fn [acc {:keys [idx] :as select-field}]
                  (let [new-list (get options (:name select-field))]
                    (if new-list
                      (assoc-in acc [idx :options] new-list)
                      acc)))
                (:fields editor) fields-with-options)]
          (assoc editor
            :mode :read
            :fields updated-fields))))))

(defn post-submit [app-atom editor-name data]
  (let [modal? (grid/modal-open? app-atom)
        editor-cfg @(editor-cursor app-atom editor-name)]
    (when modal?
      (editor-overlay app-atom :close))
    (update-config app-atom editor-cfg {:dissoc [:event]})
    (cond
      (:field-errors data)
      (editor-field-errors app-atom editor-name (:field-errors data))

      (and modal? (:error data))
      (editor-error app-atom editor-name (:error data))

      (and (not modal?) (:error data))
      (toaster/open-toast app-atom :error (:error data) {})

      modal?
      (do
        (grid/close-modal app-atom)
        {:success true})

      (not modal?)
      {:success true}

      :else
      {:success false})))

(defn update-field-config [app-atom editor-name field-name config]
  (let [editor-atom (editor-cursor app-atom editor-name)
        idx-lookup (cmn/curact editor-atom :field-idx-lookup)
        idx (int (get idx-lookup field-name))]

    (cond
      (not (yuc/presence idx))
      (toaster/open-toast app-atom :error "Index not found" {})

      (seq (select-keys config [:name :column-idx]))
      (toaster/open-toast app-atom :error "Unsupported config" {})

      :else
      (swap! editor-atom update-in [:fields idx] merge config))))

(defn index-fields [field-defs]
  (->>
    field-defs
    (map-indexed
      (fn [idx field-def]
        (assoc field-def :column-idx idx)))
    (vec)))

(defn initiate [app-atom {:keys [editor-name fields] :as config}]
  (let [editor-atom (editor-cursor app-atom editor-name)
        idx-fields (index-fields fields)
        idx-lookup
        (reduce
          (fn [acc idx-field]
            (assoc acc (:name idx-field) (:column-idx idx-field)))
          {} idx-fields)]
    (reset! editor-atom
      (assoc config
        :fields idx-fields
        :field-idx-lookup idx-lookup))))